import { useCallback, useState } from "react";
import { useBitgetWsConnection } from "./useBitgetWsConnection";
import { formatBitgetInstId, getBitgetInstType } from "./utils";

interface BitgetTickerMessage {
  action?: string;
  arg?: {
    instType: string;
    channel: string;
    instId: string;
  };
  data?: Array<{
    markPrice?: string;
    instId?: string;
    [key: string]: unknown;
  }>;
}

interface UseBitgetMarkPriceWSResult {
  markPrice: number | undefined;
  isConnected: boolean;
  error: string | null;
}

/**
 * Hook for subscribing to Bitget mark price via shared WebSocket.
 * Uses the ticker channel on the public futures WS endpoint.
 *
 * Subscribe: { op: "subscribe", args: [{ instType: "USDT-FUTURES", channel: "ticker", instId: "BTCUSDT" }] }
 * Push data includes `markPrice` field.
 */
export const useBitgetMarkPriceWS = ({
  pairSymbol,
  enabled = true,
}: {
  pairSymbol: string | undefined;
  enabled?: boolean;
}): UseBitgetMarkPriceWSResult => {
  const [markPrice, setMarkPrice] = useState<number | undefined>(undefined);
  const [markPriceError, setMarkPriceError] = useState<string | null>(null);

  const setup = useCallback(
    (ws: WebSocket) => {
      if (!pairSymbol) return;

      setMarkPrice(undefined);
      setMarkPriceError(null);

      const instId = formatBitgetInstId(pairSymbol);
      const instType = getBitgetInstType(pairSymbol);

      const subscribeMsg = {
        op: "subscribe",
        args: [{ instType, channel: "ticker", instId }],
      };

      ws.send(JSON.stringify(subscribeMsg));

      return () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ op: "unsubscribe", args: [{ instType, channel: "ticker", instId }] }));
        }
      };
    },
    [pairSymbol],
  );

  const onMessage = useCallback(
    (msg: unknown) => {
      if (!pairSymbol) return;

      try {
        const typed = msg as BitgetTickerMessage;

        // Only process ticker data pushes (action: "snapshot" or "update")
        if (!typed.action || !typed.data?.length) return;

        const expectedInstId = formatBitgetInstId(pairSymbol);
        if (typed.arg?.channel !== "ticker" || typed.arg?.instId !== expectedInstId) return;

        const priceStr = typed.data[0]?.markPrice;
        if (priceStr) {
          const price = parseFloat(priceStr);
          if (!isNaN(price)) {
            setMarkPrice(price);
            setMarkPriceError(null);
          }
        }
      } catch {
        setMarkPriceError("Failed to parse mark price update");
      }
    },
    [pairSymbol],
  );

  const { isConnected, error: connectionError } = useBitgetWsConnection({
    setup,
    onMessage,
    enabled: enabled && !!pairSymbol,
  });

  return {
    markPrice,
    isConnected,
    error: markPriceError ?? connectionError,
  };
};
